import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  PlayCircle,
  MousePointerClick,
  ArrowRightCircle,
  Image as ImageIcon,
  Upload,
  X,
  Info,
} from 'lucide-react';
import type { FlowLayerExit } from '@/store/videoFeatures/types/conversion-journey';
import { defaultFlowLayerExit } from '@/store/videoFeatures/types/conversion-journey';
import { VideoLibraryModalWithFolders } from '@/components/video-editor/VideoLibraryModalWithFolders';
import { videoService } from '@/services/videoService';
import { cn } from '@/lib/utils';

// Duração (segundos) -> "m:ss" para exibir no card do vídeo selecionado.
const formatDuration = (sec?: number) => {
  const s = Number(sec) || 0;
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60)
    .toString()
    .padStart(2, '0')}`;
};

interface Props {
  exit?: FlowLayerExit;
  onChange: (next: FlowLayerExit) => void;
  /** id do vídeo principal — pra upload de imagem e exclusão do seletor. */
  videoId?: string;
}

function Section({
  icon,
  title,
  subtitle,
  rightSlot,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  rightSlot?: React.ReactNode;
  children?: React.ReactNode;
}) {
  useLanguage();
  return (
    <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {icon && (
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{title}</h3>
            {subtitle && (
              <p className="text-[12.5px] text-[#6B7280] leading-relaxed mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {rightSlot}
      </header>
      {children}
    </section>
  );
}

const CTA_TIME_PRESETS: Array<{ id: number; label: string }> = [
  { id: 0, label: 'Desde o início' },
  { id: 5, label: 'Após 5 segundos' },
];

function CtaTimeChips({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  useLanguage();
  const [open, setOpen] = React.useState(false);
  const isCustom = !CTA_TIME_PRESETS.some((p) => p.id === value);
  const customVal = isCustom ? value : 10;
  const mins = Math.floor(customVal / 60);
  const secs = customVal % 60;
  const setMins = (m: number) => onChange(Math.max(1, Math.min(3599, m * 60 + secs)));
  const setSecs = (s: number) => onChange(Math.max(1, Math.min(3599, mins * 60 + s)));
  const fmt = (n: number) => String(Math.max(0, n)).padStart(2, '0');

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {CTA_TIME_PRESETS.map((opt) => {
        const active = !isCustom && value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] transition-colors',
              active
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            {opt.label}
          </button>
        );
      })}
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (o && !isCustom) onChange(10);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'px-3 py-2.5 rounded-[10px] border text-[12.5px] whitespace-nowrap transition-colors',
              isCustom
                ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]',
            )}
          >
            {translateInterfaceText("Personalizado")}</button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className="w-[260px] rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)]"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF]">
                {translateInterfaceText("Tempo personalizado")}</p>
              <span className="text-[11px] font-medium text-[#4B7BFF] tabular-nums">
                {fmt(mins)}:{fmt(secs)}
              </span>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Minutos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={mins}
                  onChange={(e) => setMins(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <span className="pb-4 text-2xl font-light text-[#D1D5DB]">:</span>
              <div className="flex-1">
                <label className="block text-[10.5px] font-medium text-[#6B7280] mb-1 ml-1">{translateInterfaceText("Segundos")}</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={secs}
                  onChange={(e) => setSecs(parseInt(e.target.value || '0', 10))}
                  className="h-14 px-3 text-center text-2xl font-semibold text-[#1A1A1A] tabular-nums rounded-xl border-[#E5E7EB] bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#4B7BFF]/20 focus-visible:border-[#4B7BFF] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export const ExitVideoBlock: React.FC<Props> = ({ exit, onChange, videoId }) => {
  useLanguage();
  const ex: FlowLayerExit = exit ?? defaultFlowLayerExit;
  const update = (patch: Partial<FlowLayerExit>) => onChange({ ...ex, ...patch });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Upload binário da imagem do CTA (Bunny) — nunca base64. Fallback p/ base64
  // só se ainda não houver videoId (BE faz o upload no save).
  const handleCtaImage = async (file: File) => {
    if (videoId) {
      try {
        const { fileUrl } = await videoService.uploadConversionJourneyImage(videoId, 'exit-cta', file);
        update({ ctaImage: fileUrl });
        return;
      } catch (e) {
        // cai no fallback base64 abaixo
      }
    }
    const reader = new FileReader();
    reader.onload = () => update({ ctaImage: String(reader.result) });
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-[14px] border border-[#E5E7EB] bg-white px-4 py-3">
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${
                ex.enabled ? 'bg-[#EEF2FF]' : 'bg-[#F3F4F6]'
              }`}
            >
              <ArrowRightCircle
                className={`w-[18px] h-[18px] ${ex.enabled ? 'text-[#4B7BFF]' : 'text-[#9CA3AF]'}`}
              />
            </div>
            <div className="min-w-0">
              <h3 className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Vídeo de saída")}</h3>
              <p className="text-[12px] text-[#6B7280] leading-tight mt-0.5">
                {translateInterfaceText("Exibido após o principal para apresentar sua oferta.")}</p>
            </div>
          </div>
          <Switch checked={ex.enabled} onCheckedChange={(v) => update({ enabled: v })} />
        </header>
      </section>

      {ex.enabled && (
        <>
          <div>
            <p className="text-[11.5px] font-medium text-[#6B7280] mb-2">{translateInterfaceText("Pré-visualização")}</p>
            <div className="relative rounded-[12px] overflow-hidden bg-[#0F172A] aspect-video ring-1 ring-black/5">
              {ex.libraryVideoThumbnail ? (
                <img
                  src={ex.libraryVideoThumbnail}
                  alt={ex.libraryVideoName || ''}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/60">
                  <PlayCircle className="w-8 h-8" strokeWidth={1.5} />
                  <span className="text-[11.5px]">{translateInterfaceText("Nenhum vídeo selecionado")}</span>
                </div>
              )}
              {ex.libraryVideoThumbnail && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              )}
            </div>
          </div>

          <div className="rounded-[12px] border border-[#E5E7EB] bg-[#FAFBFC] p-3 flex items-center gap-3">
            <div className="w-14 h-10 rounded-[8px] bg-[#0F172A] flex items-center justify-center shrink-0 overflow-hidden">
              {ex.libraryVideoThumbnail ? (
                <img
                  src={ex.libraryVideoThumbnail}
                  alt={ex.libraryVideoName || ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                <PlayCircle className="w-5 h-5 text-white/80" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-[#1A1A1A] truncate">
                {ex.libraryVideoName || translateInterfaceText("Selecione um vídeo da biblioteca")}
              </p>
              <p className="text-[11.5px] text-[#6B7280]">
                {ex.libraryVideoDuration ? translateInterfaceText("Duração: {value0}", { value0: (ex.libraryVideoDuration) }) : translateInterfaceText("Nenhum vídeo selecionado")}
              </p>
            </div>
            {ex.libraryVideoId && (
              <button
                type="button"
                className="h-9 px-3 rounded-[8px] text-[12.5px] text-[#6B7280] hover:text-[#1A1A1A] hover:bg-white"
                onClick={() =>
                  update({
                    libraryVideoId: null,
                    libraryVideoName: '',
                    libraryVideoDuration: '',
                    libraryVideoThumbnail: '',
                  })
                }
              >
                {translateInterfaceText("Remover")}</button>
            )}
            <button
              type="button"
              className="h-9 px-3 rounded-[8px] border border-[#E5E7EB] text-[12.5px] text-[#374151] hover:bg-white"
              onClick={() => setPickerOpen(true)}
            >
              {ex.libraryVideoId ? translateInterfaceText("Trocar") : translateInterfaceText("Selecionar")}
            </button>
          </div>

          <div className="rounded-[10px] bg-[#EFF6FF] border border-[#BFDBFE] px-3 py-2.5 flex items-start gap-2">
            <Info className="w-4 h-4 text-[#2563FF] mt-0.5 shrink-0" />
            <p className="text-[12px] text-[#1E40AF] leading-relaxed">
              <span className="font-semibold">{translateInterfaceText("Início automático:")}</span> {" "}{translateInterfaceText("o vídeo de saída começa assim que o principal termina, conduzindo o espectador para a próxima ação.")}</p>
          </div>

          <Section
            icon={<MousePointerClick className="w-5 h-5 text-[#4B7BFF]" />}
            title={translateInterfaceText("CTA do vídeo de saída")}
            subtitle={translateInterfaceText("Exiba um botão de ação durante o vídeo de saída.")}
            rightSlot={<Switch checked={ex.ctaEnabled} onCheckedChange={(v) => update({ ctaEnabled: v })} />}
          >
            {ex.ctaEnabled && (
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Imagem (favicon/logo)")}</Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <div className="relative group">
                      <label className="cursor-pointer block">
                        <div
                          className={`w-14 h-14 rounded-[12px] overflow-hidden flex items-center justify-center transition-all ${
                            ex.ctaImage
                              ? 'ring-1 ring-black/5 bg-white'
                              : 'border border-dashed border-[#D1D5DB] bg-gradient-to-br from-[#F8FAFC] to-[#EEF2F7] hover:border-[#4B7BFF] hover:from-[#F0F5FF] hover:to-[#E8EFFF]'
                          }`}
                        >
                          {ex.ctaImage ? (
                            <>
                              <img src={ex.ctaImage} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 rounded-[12px] bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <Upload className="w-4 h-4 text-white" strokeWidth={2} />
                              </div>
                            </>
                          ) : (
                            <ImageIcon className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.5} />
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleCtaImage(file);
                          }}
                        />
                      </label>
                      {ex.ctaImage && (
                        <button
                          type="button"
                          onClick={() => update({ ctaImage: '' })}
                          aria-label={translateInterfaceText("Remover imagem")}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white ring-1 ring-black/10 shadow-sm flex items-center justify-center text-[#6B7280] hover:text-[#111827] hover:ring-black/20 transition-all"
                        >
                          <X className="w-3 h-3" strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[12px] font-medium text-[#374151]">
                        {ex.ctaImage ? translateInterfaceText("Imagem carregada") : translateInterfaceText("Adicionar imagem")}
                      </span>
                      <span className="text-[11px] text-[#9CA3AF]">{translateInterfaceText("PNG, JPG ou SVG · até 2 MB")}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Título")}</Label>
                  <Input
                    value={ex.ctaTitle}
                    onChange={(e) => update({ ctaTitle: e.target.value })}
                    placeholder={translateInterfaceText("Ex: Próximo passo da sua jornada")}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Descrição (opcional)")}</Label>
                  <Input
                    value={ex.ctaDescription}
                    onChange={(e) => update({ ctaDescription: e.target.value })}
                    placeholder={translateInterfaceText("Ex: Continue agora mesmo")}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto do botão")}</Label>
                    <Input
                      value={ex.ctaText}
                      onChange={(e) => update({ ctaText: e.target.value })}
                      placeholder={translateInterfaceText("Quero acessar agora")}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[12px] text-[#374151]">{translateInterfaceText("URL de destino")}</Label>
                    <Input
                      value={ex.ctaUrl}
                      onChange={(e) => update({ ctaUrl: e.target.value })}
                      placeholder="https://seudominio.com/oferta"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[12px] text-[#374151] mb-2">{translateInterfaceText("Quando exibir o CTA")}</p>
                  <CtaTimeChips value={ex.ctaShowAfterSec} onChange={(v) => update({ ctaShowAfterSec: v })} />
                </div>
              </div>
            )}
          </Section>

          <div className="flex gap-2.5 items-start rounded-[10px] bg-[#EFF6FF] border border-[#BFDBFE] px-3 py-2.5">
            <Info className="w-4 h-4 text-[#2563FF] shrink-0 mt-0.5" />
            <p className="text-[11.5px] leading-relaxed text-[#1E40AF]">
              {translateInterfaceText("Quando o vídeo de saída terminar, o player exibe um")}{" "}<strong>{translateInterfaceText("CTA centralizado")}</strong> {" "}{translateInterfaceText("e congela a tela. O espectador não pode pular nem voltar, apenas clicar no botão para seguir.")}</p>
          </div>
        </>
      )}

      <VideoLibraryModalWithFolders
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={translateInterfaceText("Selecionar vídeo de saída")}
        onSelect={(v) =>
          update({
            sourceType: 'library',
            libraryVideoId: v.id,
            libraryVideoName: v.title,
            libraryVideoDuration: formatDuration(v.duration),
            libraryVideoThumbnail: v.thumbnailUrl ?? '',
          })
        }
      />
    </div>
  );
};

export default ExitVideoBlock;
